import { Check, ChevronRight, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { LeadFlow, FlowAction } from "@/lib/leadFlow";

interface Props {
  flow: LeadFlow;
  lostReason?: string | null;
  onAction: (action: FlowAction) => void;
  onReopen?: () => void;
}

/**
 * The 5-step checklist at the top of the lead page. Exactly one step is
 * "current" and carries the single primary CTA — the operator never has to
 * guess what comes next. Pure state comes from computeLeadFlow().
 */
export function LeadFlowStepper({ flow, lostReason, onAction, onReopen }: Props) {
  if (flow.lost) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-bad/20 bg-bad-soft px-4 py-3 text-sm text-bad">
        <div className="flex items-center gap-2">
          <XCircle className="h-4 w-4 shrink-0" />
          <span className="font-medium">
            Lead marcado como perdido{lostReason ? ` — ${lostReason}` : ""}.
          </span>
        </div>
        {onReopen && (
          <Button size="sm" variant="outline" className="shrink-0 bg-background" onClick={onReopen}>
            Reabrir lead
          </Button>
        )}
      </div>
    );
  }

  const current = flow.current;

  return (
    <section className="space-y-3 rounded-2xl border border-border bg-card px-5 py-4 shadow-card">
      <ol className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        {flow.steps.map((s, idx) => {
          const done = s.state === "done";
          const cur = s.state === "current";
          return (
            <li key={s.key} className="flex min-w-0 items-center gap-2">
              <span
                className={cn(
                  "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold",
                  done && "bg-ok text-ok-foreground",
                  cur && "bg-primary text-primary-foreground ring-4 ring-primary/15",
                  !done && !cur && "bg-muted text-muted-foreground",
                )}
                aria-current={cur ? "step" : undefined}
              >
                {done ? <Check className="h-3.5 w-3.5" /> : idx + 1}
              </span>
              <span
                className={cn(
                  "truncate text-[12.5px] leading-tight",
                  cur
                    ? "font-bold text-foreground"
                    : done
                      ? "font-semibold text-foreground/80"
                      : "text-muted-foreground",
                )}
              >
                {s.label}
              </span>
            </li>
          );
        })}
      </ol>

      {current ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/15 bg-accent/60 px-4 py-2.5">
          <div className="text-[13px] text-foreground">
            <span className="font-bold">Próximo passo:</span>{" "}
            <span className="font-semibold">{current.label}.</span>{" "}
            {current.hint && <span className="text-muted-foreground">{current.hint}</span>}
          </div>
          {current.cta && (
            <Button
              size="sm"
              className="h-8 shrink-0 rounded-lg text-xs font-semibold"
              onClick={() => onAction(current.cta!.action)}
            >
              {current.cta.label}
              <ChevronRight className="ml-1 h-3 w-3" />
            </Button>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2 rounded-xl border border-ok/20 bg-ok-soft px-4 py-2.5 text-[13px] font-medium text-ok">
          <Check className="h-4 w-4" /> Serviço entregue. Este lead está encerrado.
        </div>
      )}
    </section>
  );
}
